// The open dialogs, innermost last, and what they do to the page behind them. Modal.jsx
// renders and sequences; the bookkeeping lives here, pure over anything with an `inert`
// property and a `style`, so test/web/modal-stack.test.js can reach it without a DOM.
//
// WHY A STACK: a dialog can open over a dialog (the request form over the board). Escape
// and a backdrop click must close only the innermost, and everything behind the innermost
// — the app AND the dialog under it — must be inert, so neither Tab nor a screen reader
// can wander out of the one the person is in.
//
// While any dialog is open the page does not scroll either: `overflow: hidden` on the
// scroll root, set on the first open and put back exactly as it was on the last close.

/**
 * @param {object} page
 * @param {() => Iterable<{inert: boolean}>} page.pageChildren  the page's top-level
 *   elements, dialogs included (Modal.jsx passes document.body's children).
 * @param {() => {style: {overflow: string}}} page.scrollRoot  the element whose scroll
 *   is locked (Modal.jsx passes <html>).
 */
export function createModalStack({ pageChildren, scrollRoot }) {
  /** @type {Array<{element: object, opener: object|null}>} innermost last */
  const open = [];
  // Only what THIS stack made inert is ever un-inerted by it: an element something else
  // had already made inert stays that way when the last dialog closes.
  const madeInert = new Set();
  // The scroll root's own inline overflow before the first dialog opened; null while
  // nothing is open.
  let savedOverflow = null;

  function sync() {
    const top = open.at(-1)?.element;
    if (top === undefined) {
      for (const element of madeInert) element.inert = false;
      madeInert.clear();
      if (savedOverflow !== null) {
        scrollRoot().style.overflow = savedOverflow;
        savedOverflow = null;
      }
      return;
    }
    if (savedOverflow === null) {
      const root = scrollRoot();
      savedOverflow = root.style.overflow;
      root.style.overflow = 'hidden';
    }
    for (const element of pageChildren()) {
      if (element === top) {
        if (madeInert.has(element)) {
          element.inert = false;
          madeInert.delete(element);
        }
      } else if (!element.inert) {
        element.inert = true;
        madeInert.add(element);
      }
    }
  }

  return {
    /**
     * `element` is now the innermost dialog. `opener` is what had focus when it opened,
     * held so `close` can say where focus goes back to.
     */
    open(element, opener = null) {
      open.push({ element, opener });
      sync();
    },

    /**
     * `element` has closed. Returns what should take focus now, or null for "move
     * nothing".
     *
     * Usually the innermost closes, and its opener — in the layer below, now live again —
     * gets focus back. But an outer dialog can unmount first (a route change or a 401
     * while the form is open over the board). Then the page is still inert behind the
     * dialog above it, so focusing the outer opener now would do nothing; and the dialog
     * above was opened from INSIDE this one (everything else was inert), so its own opener
     * is going away. It inherits this dialog's opener instead, and focus moves when it
     * closes.
     */
    close(element) {
      const at = open.findLastIndex((entry) => entry.element === element);
      if (at === -1) {
        sync();
        return null;
      }
      const [closed] = open.splice(at, 1);
      const wasTop = at === open.length;
      if (!wasTop) open[at].opener = closed.opener;
      sync();
      return wasTop ? closed.opener : null;
    },

    /** True when `element` is the innermost open dialog. */
    isTop(element) {
      return open.length > 0 && open.at(-1).element === element;
    },
  };
}
